import { FormEvent, lazy, ReactNode, Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Feed from './Feed';
import Ricerca from './Ricerca';
import Login from './Login';
import { logout } from './api';
import Monitoraggi from './Monitoraggi';
import CompetitorAnalysis from './CompetitorAnalysis';
import CompetitorDetail from './CompetitorDetail';
import Report from './Report';
import Trend from './Trend';
import PageBack from './PageBack';
import Home from './Home';
import ZoneHistory from './ZoneHistory';
import ZoneCategoryDetail from './ZoneCategoryDetail';
import More from './More';
import LocalBusinessDetail from './LocalBusinessDetail';
import PagesIndex from './PagesIndex';
import Investments from './Investments';
import { useInvestMoveNotifications } from './useInvestMoveNotifications';
import DocumentsArchive from './DocumentsArchive';

const ZoneMap = lazy(() => import('./ZoneMap'));

type IconName = 'home' | 'map' | 'analysis' | 'report' | 'more';

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    home: <><path d="m4 11 8-7 8 7"/><path d="M6.5 10.5V20h11v-9.5M10 20v-6h4v6"/></>,
    map: <><path d="M12 21s6-5.8 6-11a6 6 0 1 0-12 0c0 5.2 6 11 6 11Z"/><circle cx="12" cy="10" r="2"/></>,
    analysis: <><path d="M4 18V9m5 9V5m5 13v-7m5 7V3"/><path d="M3 21h18"/></>,
    report: <><path d="M6 3h9l4 4v14H6Z"/><path d="M15 3v5h5M9 13h6m-6 4h6"/></>,
    more: <><circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none"/></>,
  };
  return <svg className="ui-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg>;
}

const primary = [
  { to: '/', label: 'Home', icon: 'home' as const, end: true, aliases: [] },
  { to: '/mappa', label: 'Mappa', icon: 'map' as const, aliases: ['/zona/', '/storico/'] },
  { to: '/competitor', label: 'Analisi', icon: 'analysis' as const, aliases: ['/pagine'] },
  { to: '/report', label: 'Report', icon: 'report' as const, aliases: [] },
  { to: '/altro', label: 'Altro', icon: 'more' as const, aliases: ['/annunci', '/trend', '/monitoraggi', '/ricerca', '/investimenti', '/documenti'] },
];

function navActive(item: typeof primary[number], pathname: string) {
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`) || item.aliases.some((alias) => pathname.startsWith(alias));
}

function SideNav({ newMoves }: { newMoves: number }) {
  const location = useLocation();
  return (
    <aside className="sidebar">
      <Link to="/" className="brand" aria-label="Osservio, vai alla Home">
        <span className="brand-mark" aria-hidden="true"><span className="brand-eye" /></span><span>Osservio</span>
      </Link>
      <nav className="side-nav" aria-label="Navigazione principale">
        {primary.map((item) => <NavLink key={item.to} to={item.to} end={item.end} className={navActive(item, location.pathname) ? 'side-link is-active' : 'side-link'}><Icon name={item.icon}/><span>{item.label}</span>{item.to === '/altro' && newMoves > 0 && <b className="invest-global-move-badge" aria-label={`${newMoves} nuove mosse InvestLab`}>{Math.min(newMoves, 99)}</b>}</NavLink>)}
      </nav>
      <div className="side-secondary">
        <button type="button" className="side-link side-logout" onClick={() => logout().finally(() => { window.location.href = '/login'; })}>Esci</button>
      </div>
      <p className="source-note">Market intelligence<br/>da fonti pubbliche</p>
    </aside>
  );
}

function MobileNav({ newMoves }: { newMoves: number }) {
  const location = useLocation();
  return <nav className="mobile-nav" aria-label="Navigazione principale">{primary.map((item) => <NavLink key={item.to} to={item.to} end={item.end} className={navActive(item, location.pathname) ? 'is-active' : undefined}><Icon name={item.icon}/><span>{item.label}</span>{item.to === '/altro' && newMoves > 0 && <b className="invest-global-move-badge" aria-label={`${newMoves} nuove mosse InvestLab`}>{Math.min(newMoves, 99)}</b>}</NavLink>)}</nav>;
}

function MobileMasthead() {
  return <header className="mobile-masthead">
    <Link to="/" className="mobile-brand" aria-label="Osservio, vai alla Home"><span className="brand-mark" aria-hidden="true"><span className="brand-eye" /></span><strong>Osservio</strong></Link>
  </header>;
}

function UtilityBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');

  useEffect(() => {
    if (location.pathname === '/') setQuery(params.get('q') ?? '');
  }, [location.pathname, params]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const value = query.trim();
    navigate(value ? `/annunci?q=${encodeURIComponent(value)}` : '/annunci');
  }

  return <header className="utility-bar">
    <form className="global-search" role="search" onSubmit={submit}>
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="6.5"/><path d="m16 16 4 4"/></svg>
      <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cerca annunci o brand" aria-label="Cerca nell’archivio annunci" />
      <kbd>Invio</kbd>
    </form>
  </header>;
}

function NotFound() {
  return <section className="empty not-found"><p className="empty-kicker">Pagina non trovata</p><h1>Questa destinazione non esiste</h1><p>Il collegamento potrebbe essere vecchio oppure incompleto.</p><div><PageBack fallback="/" label="Indietro" /><Link className="btn-primary" to="/">Vai alla Home</Link></div></section>;
}

function RootPage() {
  const [params] = useSearchParams();
  const legacyArchive = ['q', 'pageId', 'category', 'platform', 'active', 'from', 'to', 'location', 'country', 'language', 'gender', 'age', 'minReach', 'minDays', 'minVariants', 'newDays', 'sort', 'live'].some((key) => params.has(key));
  return legacyArchive ? <Navigate to={`/annunci?${params.toString()}`} replace/> : <Home/>;
}

function MapRoute() {
  return <Suspense fallback={<section className="zone-page-loading" role="status"><span className="sr-only">Caricamento mappa</span><i/><i/><i/></section>}><ZoneMap/></Suspense>;
}

function LocalCompetitorRoute() {
  const [params] = useSearchParams();
  return params.has('name') ? <LocalBusinessDetail/> : <CompetitorAnalysis key="local-search"/>;
}

export default function App() {
  const location = useLocation();
  const competitorSegment = location.pathname.match(/^\/competitor\/([^/]+)$/)?.[1] ?? null;
  const isReservedCompetitorRoute = competitorSegment != null && ['scopri', 'mercato', 'locale'].includes(competitorSegment);
  const isLocalBusinessDetail = competitorSegment === 'locale' && new URLSearchParams(location.search).has('name');
  const isCompetitorDetail = competitorSegment != null && (!isReservedCompetitorRoute || isLocalBusinessDetail);
  const moveNotifications = useInvestMoveNotifications(location.pathname !== '/login');
  // Il login vive fuori dallo shell (niente sidebar/nav)
  if (location.pathname === '/login') return <Login />;
  return (
    <div className={['app-shell', location.pathname === '/annunci' ? 'has-utility' : '', isCompetitorDetail ? 'is-detail' : '', location.pathname === '/investimenti' ? 'is-investments' : ''].filter(Boolean).join(' ')}>
      <a className="skip-link" href="#main-content">Vai al contenuto</a>
      <SideNav newMoves={moveNotifications.newCount}/>
      {location.pathname !== '/report' && !isCompetitorDetail && <MobileMasthead />}
      {location.pathname === '/annunci' && <UtilityBar />}
      <main className="workspace" id="main-content">
        <Routes>
          <Route path="/" element={<RootPage />} />
          <Route path="/annunci" element={<Feed />} />
          <Route path="/mappa" element={<MapRoute />} />
          <Route path="/storico/:targetId" element={<ZoneHistory />} />
          <Route path="/zona/:targetId/categoria/:categoryKey" element={<ZoneCategoryDetail />} />
          <Route path="/ricerca" element={<Ricerca />} />
          <Route path="/monitoraggi" element={<Monitoraggi />} />
          <Route path="/pagine" element={<PagesIndex />} />
          <Route path="/competitor" element={<CompetitorAnalysis />} />
          <Route path="/competitor/scopri" element={<CompetitorAnalysis key="discover" />} />
          <Route path="/competitor/mercato" element={<CompetitorAnalysis key="market" />} />
          <Route path="/competitor/locale" element={<LocalCompetitorRoute />} />
          <Route path="/competitor/:pageId" element={<CompetitorDetail />} />
          <Route path="/trend" element={<Trend />} />
          <Route path="/report" element={<Report />} />
          <Route path="/altro" element={<More newMoves={moveNotifications.newCount}/>} />
          <Route path="/investimenti" element={<Investments moveNotifications={moveNotifications}/>} />
          <Route path="/altro" element={<More />} />
          <Route path="/documenti" element={<DocumentsArchive />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <MobileNav newMoves={moveNotifications.newCount}/>
    </div>
  );
}
